import React, { useMemo, useState } from "react";
import { CheckCircle2, Play, RotateCcw, ScanLine } from "lucide-react";
import { batchMasterApi } from "../../api/batchMasterApi";
import { useApiQuery } from "../../hooks/useApiQuery";
import PaginatedSelect from "../../components/ui/PaginatedSelect";
import { batchApi } from "../../api/batchApi";
import ActionButton from "../../components/ui/ActionButton";
import { Card } from "../../components/ui/Card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import PlantValue from "../../components/ui/PlantValue";
import { useToast } from "../../components/ui/toast/ToastProvider";
import { useApiMutation } from "../../hooks/useApiMutation";
import { assertSuccessfulResponse, getRequestErrorMessage } from "../../services/apiMessages";
import { toBatchDetail } from "../batches/batchAdapters";
import CompleteBatchModal from "../batches/components/details/CompleteBatchModal";

const AutofilledField = ({ label, value }) => (
  <div className="space-y-1.5">
    <Label>{label}</Label>
    <Input aria-label={label} value={value ?? "Not available"} readOnly className="bg-slate-50" />
  </div>
);

const ProgressBatchDetails = ({ result, onBatchChanged, onScanNext, isVerifying }) => {
  const batch = useMemo(() => toBatchDetail(result?.record), [result?.record]);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [lineId, setLineId] = useState(result?.record?.line_id || "");
  const [machineId, setMachineId] = useState(result?.record?.machine_id || "");
  const toast = useToast();
  const { run: transition, isLoading } = useApiMutation((action) => batchApi[action](batch.uid, { line_id: lineId, machine_id: machineId }));
  const isBatch = result?.entity_type === "batch" && Boolean(batch);
  const isFinished = batch?.status === "Completed" || Boolean(batch?.raw?.dispatch_on);
  const canStart = isBatch && Boolean(batch.uid) && !isFinished && batch.status === "Created";
  const canComplete = isBatch && Boolean(batch.uid) && !isFinished && ["WIP", "Hold"].includes(batch.status);
  const canRework = canComplete;
  const plantId = result?.plant_id || batch?.raw?.plant_id;
  const optionsQuery = useApiQuery(async () => {
    const [lines, machines] = await Promise.all([
      batchMasterApi.lines(plantId), batchMasterApi.machines(plantId),
    ]);
    return { plantId, lines, machines };
  }, [plantId], { skip: !plantId || !(canStart || canRework) });
  const options = optionsQuery.data?.plantId === plantId ? optionsQuery.data : null;
  const lineOptions = (options?.lines || [])
    .filter((line) => line.plant_id === plantId)
    .map((line) => ({ value: line.line_id, label: line.line_name || line.line_id }));
  const machineOptions = (options?.machines || [])
    .filter((machine) => machine.plant_id === plantId && machine.line_id === lineId)
    .map((machine) => ({ value: machine.machine_id, label: machine.machine_name || machine.machine_id }));
  const validAssignment = !optionsQuery.isFetching && !optionsQuery.error &&
    lineOptions.some((line) => line.value === lineId) &&
    machineOptions.some((machine) => machine.value === machineId);


  const handleTransition = async (action) => {
    if ((action === "start" && !canStart) || (action === "rework" && !canRework)) return;
    if (isLoading || !validAssignment) return;
    try {
      const response = assertSuccessfulResponse(await transition(action), "Unable to update batch.");
      const updatedRecord = Array.isArray(response?.data) ? response.data[0] : response?.data || response;
      if (!updatedRecord?.batch_id) throw new Error("The batch update could not be confirmed. Scan the batch again.");
      setErrorMessage("");
      toast.success(`${batch.batchId} ${action === "start" ? "started" : "sent for rework"}.`);
      if (action === "start") {
        onScanNext();
      } else {
        onBatchChanged(response);
      }
    } catch (error) {
      setErrorMessage(getRequestErrorMessage(error, "Unable to update batch."));
    }
  };

  if (!isBatch) {
    return (
      <Card className="flex min-h-[240px] flex-col items-center justify-center gap-3 p-6 text-center lg:min-h-[420px]">
        <ScanLine className="h-10 w-10 text-primary" />
        <h2 className="text-base font-semibold text-slate-900">Batch Detail</h2>
        <p className="max-w-sm text-sm text-slate-500" role="status">
          {isVerifying ? "Fetching batch details…" : result ? "Scan a batch QR code to record production progress." : "Scan a batch QR code or select a plant in the top bar and enter its batch ID to start, rework, or complete a batch."}
        </p>
      </Card>
    );
  }

  return (
    <Card className="flex min-h-[420px] flex-col p-4 sm:p-5">
      <div className="flex items-center justify-between gap-3 border-b border-primary/15 pb-4">
        <h2 className="font-semibold text-primary">Batch Detail</h2>
        <span className={`rounded-full px-3 py-1 text-xs font-semibold ${isFinished ? "bg-emerald-50 text-emerald-700" : "bg-primary/10 text-primary"}`}>
          {batch.raw?.dispatch_on ? "Dispatched" : batch.status}
        </span>
      </div>

      <div className="mt-4 space-y-4">
        <PlantValue plant={batch.raw?.plant_detail} fallbackPlantId={batch.raw?.plant_id || result.plant_id} />
        <AutofilledField label="Batch ID" value={batch.batchId} />
        <AutofilledField label="Part ID (Autofilled)" value={batch.raw?.item_id ?? batch.raw?.part_id} />
        <AutofilledField label="Quantity (Autofilled)" value={batch.totalParts} />
        {(canStart || canRework) && (
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Line</Label>
              <PaginatedSelect value={lineId} options={lineOptions}
                onChange={(value) => { setLineId(value); setMachineId(""); setErrorMessage(""); }}
                disabled={isLoading || optionsQuery.isFetching || Boolean(optionsQuery.error)}
                placeholder={optionsQuery.isFetching ? "Loading lines..." : "Select line"} />
            </div>
            <div className="space-y-1.5">
              <Label>Machine</Label>
              <PaginatedSelect value={machineId} options={machineOptions}
                onChange={(value) => { setMachineId(value); setErrorMessage(""); }}
                disabled={!lineId || isLoading || optionsQuery.isFetching || Boolean(optionsQuery.error)}
                placeholder={!lineId ? "Select line first" : machineOptions.length ? "Select machine" : "No machines for this line"} />
            </div>
            {optionsQuery.error && <p role="alert" className="text-sm text-red-600">
              Unable to load lines and machines. <button type="button" onClick={optionsQuery.refetch} className="underline">Retry</button>
            </p>}
          </div>
        )}
        {!canStart && (
          <div className="grid gap-4 sm:grid-cols-2">
            <AutofilledField label="OK Parts (Autofilled)" value={batch.okParts} />
            <AutofilledField label="Hold (Autofilled)" value={batch.holdParts ?? batch.raw?.hold_parts} />
          </div>
        )}
        {isFinished && (
          <div className="grid gap-4 sm:grid-cols-2">
            <AutofilledField label="Rework Parts" value={batch.reworkParts ?? batch.raw?.rework_parts} />
            <AutofilledField label="Reject Parts" value={batch.rejectParts ?? batch.raw?.reject_parts} />
          </div>
        )}
        {isFinished && <p className="rounded-md bg-emerald-50 p-3 text-sm text-emerald-700">This batch is {batch.raw?.dispatch_on ? "dispatched" : "completed"}.</p>}
        {!canStart && !canComplete && !isFinished && <p className="rounded-md bg-amber-50 p-3 text-sm text-amber-700">No production action is available for this batch status.</p>}
        {errorMessage && <p role="alert" className="rounded-md bg-red-50 p-3 text-sm text-red-600">{errorMessage}</p>}
      </div>

      <div className="mt-auto flex flex-wrap gap-2 border-t border-slate-100 pt-5">
        <ActionButton action="cancel" icon={ScanLine} label={isFinished ? "Scan Next Batch" : "Scan Another"} onClick={onScanNext} disabled={isLoading} className="flex-1" />
        {canStart && <ActionButton action="confirm" icon={Play} label="Start Batch" onClick={() => handleTransition("start")} disabled={isVerifying || !validAssignment} isLoading={isLoading} className="flex-1 bg-emerald-600 hover:bg-emerald-700" />}
        {canRework && <ActionButton action="confirm" icon={RotateCcw} label="Rework Batch" variant="outline" onClick={() => handleTransition("rework")} disabled={isVerifying || !validAssignment} isLoading={isLoading} className="flex-1 border-orange-200 text-orange-700 hover:bg-orange-50" />}
        {canComplete && <ActionButton action="confirm" icon={CheckCircle2} label="Complete Batch" onClick={() => setCompleteOpen(true)} disabled={isLoading || isVerifying} className="flex-1" />}
      </div>

      <CompleteBatchModal open={completeOpen} batch={batch} onClose={() => setCompleteOpen(false)} onCompleted={onBatchChanged} />
    </Card>
  );
};

export default ProgressBatchDetails;
